import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertCircle, CheckCircle2, Clock, AlertTriangle, Loader2,
  ChevronRight, Filter, RefreshCw, Image, ArrowLeft
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth check hook for support pages
const useAuthCheck = (navigate) => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const userInfo = localStorage.getItem('userInfo');
      
      if (!token || !userInfo) {
        toast.error('Please login to access the Support Dashboard');
        navigate('/login');
        return;
      }

      try {
        const user = JSON.parse(userInfo);
        if (user.role !== 'internal_team' && user.role !== 'admin') {
          toast.error('Access denied. Internal team role required.');
          navigate('/login');
          return;
        }
        setIsAuthorized(true);
      } catch (e) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [navigate]);

  return { isAuthorized, isChecking };
};

const statusConfig = {
  reported: { label: 'Reported', color: 'bg-gray-100 text-gray-800', icon: AlertCircle },
  diagnosing: { label: 'Diagnosing', color: 'bg-blue-100 text-blue-800 animate-pulse', icon: Loader2 },
  fixing: { label: 'Applying Fix', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  pending_approval: { label: 'Awaiting Approval', color: 'bg-amber-100 text-amber-800', icon: Clock },
  fixed: { label: 'Fixed', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  deployed: { label: 'Deployed', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  verified: { label: 'Verified', color: 'bg-emerald-200 text-emerald-900', icon: CheckCircle2 },
  escalated: { label: 'Escalated', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  closed: { label: 'Closed', color: 'bg-gray-200 text-gray-700', icon: CheckCircle2 }
};

const categoryLabels = {
  unable_to_load: 'Unable to Load',
  answers_wiped: 'Answers Wiped',
  email_not_received: 'Email Not Received',
  wrong_assessment_content: 'Wrong Content',
  login_error: 'Login Error',
  progress_not_saving: 'Progress Not Saving',
  display_error: 'Display Error',
  missing_from_system: 'Missing from System',
  other: 'Other'
};

export default function IssueList() {
  const navigate = useNavigate();
  const { isAuthorized, isChecking } = useAuthCheck(navigate);
  
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const token = localStorage.getItem('token');

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '25'
      });
      
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);

      const response = await fetch(`${API_URL}/api/support/issues?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIssues(data.issues);
        setTotalPages(data.totalPages);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching issues:', error);
      toast.error('Failed to load issues');
    } finally {
      setLoading(false);
    }
  }, [token, page, statusFilter, categoryFilter]);

  useEffect(() => {
    if (!isAuthorized) return;
    
    fetchIssues();
    
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchIssues, 10000);
    return () => clearInterval(interval);
  }, [fetchIssues, isAuthorized]);

  // Show loading while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Don't render if not authorized (will redirect)
  if (!isAuthorized) {
    return null;
  }

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.reported;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} font-normal flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="issue-list">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/support')}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Support Issues</h1>
              <p className="text-gray-600 mt-1">Track and manage reported issues</p>
            </div>
          
          <div className="flex gap-3">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="reported">Reported</SelectItem>
                <SelectItem value="diagnosing">Diagnosing</SelectItem>
                <SelectItem value="fixing">Applying Fix</SelectItem>
                <SelectItem value="pending_approval">Awaiting Approval</SelectItem>
                <SelectItem value="fixed">Fixed</SelectItem>
                <SelectItem value="deployed">Deployed</SelectItem>
                <SelectItem value="escalated">Escalated</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(1); }}>
              <SelectTrigger className="w-48" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="unable_to_load">Unable to Load</SelectItem>
                <SelectItem value="answers_wiped">Answers Wiped</SelectItem>
                <SelectItem value="email_not_received">Email Not Received</SelectItem>
                <SelectItem value="progress_not_saving">Progress Not Saving</SelectItem>
                <SelectItem value="missing_from_system">Missing from System</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>

            <Button 
              variant="outline" 
              size="icon"
              onClick={fetchIssues}
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
        </div>

        {/* Issues List */}
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="flex items-center justify-between">
              <span>Issues ({total})</span>
              <Button 
                onClick={() => navigate('/support/issues/new')}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="new-issue-btn"
              >
                Report New Issue
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : issues.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No issues found</p>
              </div>
            ) : (
              <div className="divide-y">
                {issues.map((issue) => (
                  <div
                    key={issue.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                    onClick={() => navigate(`/support/issues/${issue.id}`)}
                    data-testid={`issue-row-${issue.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-xs text-gray-400">
                          #{issue.id.substring(0, 8)}
                        </span>
                        {getStatusBadge(issue.status)}
                        <Badge variant="outline">
                          {categoryLabels[issue.issueCategory] || issue.issueCategory}
                        </Badge>
                        {issue.hasScreenshot && (
                          <Image className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                      <h3 className="font-medium text-gray-900">{issue.employeeName}</h3>
                      <p className="text-sm text-gray-500">
                        {issue.employeeEmail} • {issue.employeeRegion || 'Unknown region'}
                      </p>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-1">
                        {issue.issueDescription}
                      </p>
                    </div>
                    
                    <div className="text-right ml-4">
                      <p className="text-sm text-gray-500">{formatTimeAgo(issue.createdAt)}</p>
                      <p className="text-xs text-gray-400 mt-1">by {issue.reportedBy}</p>
                      <ChevronRight className="w-5 h-5 text-gray-300 ml-auto mt-2" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
